import * as BABYLON from 'babylonjs';
import 'babylonjs-loaders';
import { Engine, Scene, Mesh, Vector3, Color4, StandardMaterial, MeshBuilder } from '@babylonjs/core';
import { CSS3DObject, CSS3DRenderer } from './CSS3DRenderer';
import { clamp } from 'lodash';
import YouTubePlayer  from 'youtube-player';
import { YouTubeMedia } from '../Types';
import PlayerStates from 'youtube-player/dist/constants/PlayerStates';

export class YouTubePlane {
    private scene: Scene;
    private engine: Engine;
    private CSSobject: CSS3DObject | null = null;
    private static count = 0;
    public plane: Mesh;
    private videoId: string;

    private player: any = null;
    private playbackRate = 1;

    private renderer: CSS3DRenderer | null = null;
    private divRenderer: HTMLDivElement | null = null;
    private divObject: HTMLDivElement | null = null;
    private iframe: HTMLIFrameElement | null = null;

    public dispose() {
        if (this.divRenderer !== null) this.divRenderer.remove();
        if (this.divObject !== null) this.divObject.remove();
        if (this.iframe !== null) this.iframe.remove();
        // if (this.plane !== null) this.plane.dispose();
    }

    constructor(engine: Engine, scene: Scene, videoId: string) {
        this.engine = engine;
        this.scene = scene;
        this.plane = this.createPlane();
        this.videoId = videoId;
        // this.setup();
    }

    private createPlane() {
        const plane = MeshBuilder.CreatePlane(`yt-${YouTubePlane.count}`, { width: 4, height: 3 }, this.scene);
        plane.rotationQuaternion = null;
        plane.scaling.x = 1;
        plane.scaling.y = 1;
        plane.checkCollisions = true;
        return plane;
    }

    public updateVideoId(videoId: string) {
        if (this.videoId === videoId) return;
        this.videoId = videoId;
        this.dispose();
        this.setup();
    }

    public async getYouTubeMedia() {
        const duration = await this.player.getDuration();
        const currentTime = await this.player.getCurrentTime();
        const playerState = await this.player.getPlayerState();

        const youtubeMedia: YouTubeMedia = {
            duration,
            videoId: this.videoId,
            player: this.player,
            playbackRate: this.playbackRate,
            currentSeek: currentTime,
            isPaused: playerState !== PlayerStates.PLAYING,
        };
        return youtubeMedia;
    }

    public async setPlaybackRate(rate: number) {
        this.playbackRate = rate;
        if (this.player === null) return;
        const availablePlaybackRates: number[] = await this.player.getAvailablePlaybackRates();
        if (availablePlaybackRates.includes(rate)) {
            this.player.setPlaybackRate(rate);
        }
    }

    public setMuted(mute: boolean) {
        if (this.player === null) return;
        if (mute) this.player.mute();
        else this.player.unMute();
    }

    public setPause(pause: boolean) {
        if (this.player === null) return;
        if (pause) this.player.pauseVideo();
        else this.player.playVideo();
    }

    public async seekTo(seek: number) {
        if (this.player === null) return;
        const duration = await this.player.getDuration();
        this.player.seekTo(duration * seek);
        console.log('yt plane seeking to: ', seek, duration, seek * duration);
    }

    public async increaseVolume() {
        this.updateVolumeStep(5);
    }

    public async decreaseVolume() {
        this.updateVolumeStep(-5);
    }

    private async updateVolumeStep(val: number) {
        if (this.player === null) return;
        const volume = await this.player.getVolume();
        const updatedVolume = clamp(volume + val, 0, 100);
        this.player.setVolume(updatedVolume);
    }

    public setup() {
        this.scene.clearColor = new Color4(0, 0, 0, 0);

        if (this.plane === null || this.plane.isDisposed()) this.plane = this.createPlane();
        console.log('setup plane', this.plane);
        this.setupRenderer();
        this.createCSSobject();
        this.createMaskingScreen();
        // YouTubePlane.count++;
    }

    private setupRenderer() {
        this.divRenderer = document.createElement('div');
        this.divRenderer.id = `css-container-${YouTubePlane.count}`;
        this.divRenderer.style.position = 'absolute';
        this.divRenderer.style.width = '100%';
        this.divRenderer.style.height = '100%';
        this.divRenderer.style.zIndex = '-1';


        this.renderer = new CSS3DRenderer();
        this.divRenderer.appendChild(this.renderer.domElement);

        console.log('created renderer', this.renderer);

        let canvasZone = document.getElementById('world-container');
        if (canvasZone !== null) {
          canvasZone.insertBefore(this.divRenderer, canvasZone.firstChild);
  
          this.renderer.setSize(canvasZone.offsetWidth, canvasZone.offsetHeight);
          console.log('render size set', canvasZone.offsetWidth, canvasZone.offsetHeight);
        }
    }

    private refreshPosition(): void {
        if (this.CSSobject === null) return;
        this.CSSobject.position.copyFrom(this.plane.getAbsolutePosition());
        this.CSSobject.scaling.copyFrom(this.plane.scaling);
    }

    private createCSSobject(): void {
        const buffer = 40;
        let width = 1920 + buffer;
        let height = 1080 + buffer;
        this.scene.onBeforeRenderObservable.add(() => {
            if (this.renderer !== null) this.renderer.render(this.scene, this.scene.activeCamera);
        });

        this.divObject = document.createElement('div');
        this.divObject.style.width = `${width}px`;
        this.divObject.style.height = `${height}px`;
        this.divObject.style.backgroundColor = '#000';
        this.divObject.style.zIndex = '1';
        this.CSSobject = new CSS3DObject(this.divObject, this.scene);
        this.refreshPosition();

        this.iframe = document.createElement('iframe');
        this.iframe.id = `video-${YouTubePlane.count}-${this.videoId}`;
        this.iframe.style.width = `${width}px`;
        this.iframe.style.height = `${height}px`;
        this.iframe.style.border = '0px';
        this.iframe.allow = 'autoplay';
        this.iframe.src = `https://www.youtube.com/embed/${this.videoId}?rel=0&enablejsapi=1&disablekb=1&autoplay=1&controls=0&fs=0&modestbranding=1`;
        this.divObject.appendChild(this.iframe);

        this.player = YouTubePlayer(this.iframe, { videoId: this.videoId, width, height, playerVars: { autoplay: 1, origin: 'https://localhost:3000' }});
        console.log(this.player);
        // this.player.setVolume(30);

        this.player.on('ready', async () => {
          await new Promise(r => setTimeout(r, 3000));
        //   this.player.seekTo(0, true);
          this.player.playVideo();
        //   console.log('seeked');
        });
        this.setPlaybackRate(this.playbackRate);
    }

    public updateVolume(value: number) {
        if (this.player === null) return;
        const val = value < 0 ? 0 : value > 100 ? 100 : value;
        this.player.setVolume(val);
    }
    /*
    export interface Options {
    width?: number | string | undefined;
    height?: number | string | undefined;
    videoId?: string | undefined;
    host?: string | undefined;
    playerVars?: {
        autoplay?: 0 | 1 | undefined;
        cc_lang_pref?: string | undefined;
        cc_load_policy?: 1 | undefined;
        color?: "red" | "white" | undefined;
        controls?: 0 | 1 | undefined;
        disablekb?: 0 | 1 | undefined;
        enablejsapi?: 0 | 1 | undefined;
        end?: number | undefined;
        fs?: 0 | 1 | undefined;
        hl?: string | undefined;
        iv_load_policy?: 1 | 3 | undefined;
        list?: string | undefined;
        listType?: "playlist" | "search" | "user_uploads" | undefined;
        loop?: 0 | 1 | undefined;
        modestbranding?: 1 | undefined;
        origin?: string | undefined;
        playlist?: string | undefined;
        playsinline?: 0 | 1 | undefined;
        rel?: 0 | 1 | undefined;
        start?: number | undefined;
        widget_referrer?: string | undefined;
    } | undefined;
    events?:
        | {
            [eventType in EventType]?: (event: CustomEvent) => void;
        }
        | undefined;
}
    */
    private createMaskingScreen(): void {
        let depthMask = new StandardMaterial('matDepthMask', this.scene);
        depthMask.backFaceCulling = false;
        this.plane.material = depthMask;
        this.plane.onBeforeRenderObservable.add(() => this.engine.setColorWrite(false));
        this.plane.onAfterRenderObservable.add(() => this.engine.setColorWrite(true));

        // this.scene.meshes.forEach(m => console.log(m.name));
        // const sky_index = this.scene.meshes.findIndex(m => m.name.indexOf('sky') !== -1);
        // const sky = this.scene.meshes[sky_index];
        // const mask_index = this.scene.meshes.indexOf(this.plane);
        // this.scene.meshes[mask_index] = this.scene.meshes[1];
        // this.scene.meshes[sky_index] = this.scene.meshes[0];
        // this.scene.meshes[0] = sky;
        // this.scene.meshes[1] = this.plane;
        // console.log('-------');
        // this.scene.meshes.forEach(m => console.log(m.name));
    }
}
